import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import Detail from '../../../../../types/detail/Detail';
import DetailWriteDTO from '../../../../../types/detail/DetailWriteDTO';
import SampleCategory from '../../../../../types/sample-category/SampleCategory';
import { CanManageSampleDefinitionsDirective } from '../../../directives/can-manage-sample-definitions.directive';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { DetailService } from '../../../services/detail.service';
import { SampleCategoryService } from '../../../services/sample-category.service';
import {
    SetSampleCategoriesDialogComponent
} from '../../shared/details/set-sample-categories-dialog/set-sample-categories-dialog.component';
import { UpdateDetailDialogComponent } from '../../shared/details/update-detail-dialog/update-detail-dialog.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

@Component({
    selector: 'app-detail-detail',
    templateUrl: './detail-detail.component.html',
    styleUrls: [ './detail-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateDetailDialogComponent,
        CanManageSampleDefinitionsDirective,
        SetSampleCategoriesDialogComponent,
        SharedModule,
        TableModule,
        Tooltip,
        RoleNamePipe
    ]
})
export class DetailDetailComponent implements OnInit {
    public detail: Detail | null = null;
    public categories: SampleCategory[] = [];
    public editableDetail: DetailWriteDTO = new DetailWriteDTO({});
    public displayUpdateDialog = false;
    public displaySetCategoriesDialog = false;

    constructor(
        private route: ActivatedRoute,
        private detailService: DetailService,
        private categoryService: SampleCategoryService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public async ngOnInit(): Promise<void> {
        await this.loadDetail();
    }

    /**
     * Opens the edit dialog.
     */
    public openEditDialog(): void {
        this.editableDetail = new DetailWriteDTO(this.detail ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the detail information.
     */
    public async reloadDetail(): Promise<void> {
        await this.loadDetail();
    }

    /**
     * Removes a category from the detail.
     * @param  category The category to remove.
     */
    public removeCategory(category: SampleCategory) {
        this.detailService.removeCategoryFromDetail(this.detail!.id, category.id).then(() => {
            this.loadCategories();
        }).catch(error => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000
            });
        });
    }

    /**
     * Confirms the deletion of the detail.
     */
    public confirmDelete(): void {
        if (!this.detail) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the detail '${ this.detail.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await this.detailService.deleteDetail(this.detail!.id);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Detail deleted successfully',
                        life: 3000
                    });
                    // Navigate back to the details list
                    window.history.back();
                } catch (error: any) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000
                    });
                }
            }
        });
    }

    /**
     * Loads the sample categories associated with the detail.
     */
    public loadCategories(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            this.categoryService.getSampleCategoriesByDetailId(idNumber).then((categories) => {
                this.categories = categories;
            }).catch(error => {
                this.messageService.add(
                    { severity: 'error', summary: 'Failed to load sample categories', detail: error.message });
            })
        }
    }

    /**
     * Opens the dialog to set sample categories.
     */
    public openSetSampleCategoriesDialog() {
        this.displaySetCategoriesDialog = true;
    }

    /**
     * Loads the detail information based on the route parameter.
     */
    private async loadDetail(): Promise<void> {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            try {
                this.detail = await this.detailService.getDetail(idNumber);
                this.loadCategories();
            } catch (error: any) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000
                });
            }
        }
    }
}
